import type { TaskDetails } from '../../features/audit-whiz/logic/reviewTypes';
import type { TaskDetailsValue } from '../ui/forms/TaskDetailsFields';
import { isHtmlContentEmpty } from 'src/validations/translationFormSchema';

/**
 * Shared task-details helpers + validation for the AuditWhiz review flow. A
 * "task" is the SAME Helios {@link TaskDetails} shape wherever it appears — on a
 * sub-question (`sub.taskDetails`) or on a question's Audit Criteria
 * (`question.content.taskDetails`). Both surfaces hide the task behind an
 * "Add Task" toggle and, once attached, require all three fields; this module
 * is the single implementation both read from so the two can never drift.
 *
 * Pure — no react, no side effects. Container-agnostic: everything here takes
 * the task value (or `undefined`), never the sub-question / question wrapper.
 */

/** An empty task shell used when a surface GAINS a task (the Add Task click).
 *  Module-level for reference stability across renders. The duration key is
 *  omitted so it round-trips under exactOptionalPropertyTypes. */
export const EMPTY_TASK_DETAILS: TaskDetails = { taskName: '', taskDescription: '' };

/** Normalize a {@link TaskDetailsValue} (the fields component's edit shape) to
 *  the Helios {@link TaskDetails}, omitting the duration key when absent
 *  (exactOptionalPropertyTypes-clean). */
export function toTaskDetails(next: TaskDetailsValue): TaskDetails {
  if (next.taskDurationDays === undefined) {
    return { taskName: next.taskName, taskDescription: next.taskDescription };
  }
  return {
    taskName: next.taskName,
    taskDescription: next.taskDescription,
    taskDurationDays: next.taskDurationDays,
  };
}

/**
 * Returns `container` without its `taskDetails` key (rather than setting it to
 * `undefined`) so it stays clean under exactOptionalPropertyTypes. Generic over
 * the container so both a sub-question and a question's content can drop their
 * task through one implementation (the Remove Task click).
 */
export function stripTaskDetails<T extends { taskDetails?: TaskDetails }>(
  container: T,
): T {
  const entries = Object.entries(container).filter(([key]) => key !== 'taskDetails');
  return Object.fromEntries(entries) as unknown as T;
}

/** Which required field of an attached task is blank. The caller names only the
 *  fields actually missing (per-field inline errors + the publish banner), so a
 *  partial fill flags only what's left. */
export const TaskField = {
  NAME: 'NAME',
  DESCRIPTION: 'DESCRIPTION',
  DURATION: 'DURATION',
} as const;

export type TaskField = (typeof TaskField)[keyof typeof TaskField];

/**
 * The required task fields that are blank, in display order (name, description,
 * duration) — empty when the task is complete OR absent. The canonical
 * "a task is OPTIONAL, but once attached its name + description + positive
 * duration are required" rule, shared by sub-questions and audit criteria.
 *
 * Description is a rich-text (HTML) field, so a visually-cleared editor stores
 * `"<p></p>"`; it's checked with the canonical {@link isHtmlContentEmpty} using
 * `allowImageOnly` (the manual flow lets a description be only a pasted
 * screenshot — matches the V1 sub-question task rule).
 */
export function missingTaskFields(task: TaskDetails | undefined): readonly TaskField[] {
  if (!task) return [];
  const missing: TaskField[] = [];
  if (task.taskName.trim().length === 0) missing.push(TaskField.NAME);
  if (isHtmlContentEmpty(task.taskDescription, { allowImageOnly: true })) {
    missing.push(TaskField.DESCRIPTION);
  }
  if ((task.taskDurationDays ?? 0) <= 0) missing.push(TaskField.DURATION);
  return missing;
}

/** Per-field inline-error flags for an attached task's three fields — true when
 *  that field is blocking publish. Shared selector so a sub-question row and the
 *  audit-criteria step derive identical flags from one `missingTaskFields`
 *  result. */
export interface TaskFieldErrors {
  readonly taskName: boolean;
  readonly taskDescription: boolean;
  readonly taskDuration: boolean;
}

/** Derive the per-field booleans from a `missingTaskFields` list. */
export function taskFieldErrors(missing: readonly TaskField[]): TaskFieldErrors {
  return {
    taskName: missing.includes(TaskField.NAME),
    taskDescription: missing.includes(TaskField.DESCRIPTION),
    taskDuration: missing.includes(TaskField.DURATION),
  };
}
